// src/utils/inputValidation.ts

// Email validation
export const validateEmail = (email: string): boolean => {
  // This regex is more comprehensive and follows RFC 5322 standards
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return emailRegex.test(email);
};

// Password validation
export const validatePassword = (password: string): boolean => {
  // Require at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
  return passwordRegex.test(password);
};

// Input sanitization
export const sanitizeInput = (input: unknown): string => {
  // If input is not a string, convert it to a string
  const stringInput = String(input);
  // Remove HTML tags, trim whitespace, and escape special characters
  return stringInput
    .replace(/(<([^>]+)>)/gi, "")
    .trim()
    .replace(/[&<>"']/g, function (m) {
      return (
        {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }[m] || m
      );
    });
};

// Name validation
export const validateName = (name: string): boolean => {
  // Allow letters, spaces, hyphens, and apostrophes, minimum 2 characters
  const nameRegex = /^[a-zA-Z\s-']{2,}$/;
  return nameRegex.test(name);
};

// Event ID validation
export const validateEventId = (eventId: string): boolean => {
  // Allow alphanumeric characters and hyphens, minimum 3 characters, maximum 50
  const eventIdRegex = /^[a-zA-Z0-9-]{3,50}$/;
  return eventIdRegex.test(eventId);
};

// Date validation
export const validateDate = (date: string): boolean => {
  // Check if the date is not in the past and is a valid date
  const inputDate = new Date(date);
  const currentDate = new Date();
  return !isNaN(inputDate.getTime()) && inputDate >= currentDate;
};

// Time validation
export const validateTime = (time: string): boolean => {
  // Time format validation (HH:MM)
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
  return timeRegex.test(time);
};

// Phone number validation
export const validatePhoneNumber = (phoneNumber: string): boolean => {
  // This regex allows for various formats of international phone numbers
  // It expects the phone number to start with a '+' followed by 1-3 digits for the country code,
  // then allows for optional spaces, hyphens, or periods, followed by the rest of the number
  const phoneRegex = /^\+[1-9]\d{1,3}[\s.-]?\d{1,14}$/;
  return phoneRegex.test(phoneNumber);
};
// Instagram handle validation
export const validateInstagramHandle = (handle: string): boolean => {
  // Allow letters, numbers, periods, and underscores, 1-30 characters
  const instagramRegex = /^[a-zA-Z0-9_.]{1,30}$/;
  return instagramRegex.test(handle);
};

// URL validation
export const validateURL = (url: string): boolean => {
  const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
  return urlRegex.test(url);
};

// Numeric input validation
export const validateNumeric = (input: string): boolean => {
  return !isNaN(Number(input));
};

// Range validation
export const validateRange = (
  value: number,
  min: number,
  max: number
): boolean => {
  return value >= min && value <= max;
};

// Length validation
export const validateLength = (
  input: string,
  minLength: number,
  maxLength: number
): boolean => {
  return input.length >= minLength && input.length <= maxLength;
};

// Custom regex validation
export const validateCustomRegex = (input: string, regex: RegExp): boolean => {
  return regex.test(input);
};

// Utility function to combine multiple validators
export const combineValidators = (
  ...validators: ((input: string) => boolean)[]
) => {
  return (input: string) => validators.every((validator) => validator(input));
};

// Utility function to get validation error messages
export const getValidationErrorMessage = (validatorName: string): string => {
  const errorMessages: { [key: string]: string } = {
    email: "Please enter a valid email address.",
    password:
      "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.",
    name: "Please enter a valid name (letters, spaces, hyphens, and apostrophes only).",
    eventId:
      "Event ID must be 3-50 characters long and can only contain letters, numbers, and hyphens.",
    date: "Please select a future date.",
    time: "Please enter a valid time in HH:MM format.",
    phoneNumber: "Please enter a valid international phone number.",
    instagramHandle:
      "Please enter a valid Instagram handle (1-30 characters, letters, numbers, periods, and underscores only).",
    url: "Please enter a valid URL.",
    numeric: "Please enter a valid number.",
    range: "Please enter a value within the specified range.",
    length: "Please enter input of the correct length.",
  };

  return errorMessages[validatorName] || "Invalid input.";
};
